import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatDatepickerModule } from '@angular/material/datepicker'
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatIconButton } from '@angular/material/button'
import { MatTooltip } from '@angular/material/tooltip'
import { ParamMap } from '@angular/router'
import { TranslatePipe } from '@ngx-translate/core'
import { endOfDay, startOfDay } from 'date-fns'

export type DateRange = { startDate: Date | null; endDate: Date | null }

export const EMPTY_DATE_RANGE: DateRange = { startDate: null, endDate: null }

/**
 * Reads a range off the URL, for the screens that keep their filters in query parameters.
 *
 * A parameter that is not a date reads as no date at all: the values come from the address bar, so a
 * hand-edited or truncated one would otherwise reach the request and throw on serialisation.
 */
export function readDateRangeFromQueryParams(queryParamMap: ParamMap, startKey = 'startDate', endKey = 'endDate'): DateRange {
  const parse = (value: string | null): Date | null => {
    if (value === null) {
      return null
    }
    const date = new Date(value)
    return isNaN(date.getTime()) ? null : date
  }
  return { startDate: parse(queryParamMap.get(startKey)), endDate: parse(queryParamMap.get(endKey)) }
}

/**
 * One control for a from–to range, so that a screen filtering by date does not wire up two pickers
 * and keep them consistent itself.
 *
 * Either end may be left empty: an open-ended range filters on the end it has. The dates are widened
 * to whole days, so a range reads the way it looks — picking the same day at both ends covers that
 * day rather than a zero-length instant at midnight.
 */
@Component({
  selector: 'app-date-range-picker',
  standalone: true,
  imports: [FormsModule, MatDatepickerModule, MatFormField, MatIcon, MatIconButton, MatLabel, MatSuffix, MatTooltip, TranslatePipe],
  templateUrl: './date-range-picker.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './date-range-picker.component.scss',
})
export class DateRangePickerComponent {
  @Input()
  startDate: Date | null = null

  @Input()
  endDate: Date | null = null

  @Input()
  labelTranslationKey = 'COMMON.DATE_RANGE'

  @Output()
  rangeChange = new EventEmitter<DateRange>()

  get hasRange(): boolean {
    return this.startDate !== null || this.endDate !== null
  }

  // Both ends of a range are picked in one go, well before the parent has echoed the first one back
  // through the inputs, so the component holds its own state rather than reading a stale input.
  setStartDate(startDate: Date | null) {
    this.startDate = startDate === null ? null : startOfDay(startDate)
    this.emitRange()
  }

  setEndDate(endDate: Date | null) {
    this.endDate = endDate === null ? null : endOfDay(endDate)
    this.emitRange()
  }

  clear() {
    this.startDate = null
    this.endDate = null
    this.emitRange()
  }

  private emitRange() {
    this.rangeChange.emit({ startDate: this.startDate, endDate: this.endDate })
  }
}
